import { Badge, Group, Progress, Stack, Table, Text, Tooltip } from "@mantine/core";
import { METRICS, MetricDelta } from "../_Shared/metricDelta";

/**
 * The targets table: where each one stands, and how much of it to believe.
 *
 * Two rules run through this file, and both exist because the alternative is a confident lie.
 *
 * A target whose state is not measured renders no number and no arrow. `unmeasurable` and
 * `insufficient` are statements about the measurement, not positions on a scale, so a progress bar or
 * a direction arrow on one would be a claim the data does not support — and 0 is the best possible
 * search position, which makes "no data" and "we won" the same pixel.
 *
 * Every measured value carries the day it actually describes. Search Console lags about two days and
 * its daily series is refreshed weekly, so a figure shown today can be over a week old.
 */

/** Backend TargetState, camelCased by the API's enum converter. */
const STATE = {
  unmeasurable: { color: "gray", label: "Can't measure" },
  insufficient: { color: "yellow", label: "Too little data" },
  regressed: { color: "red", label: "Going backwards" },
  flat: { color: "gray", label: "No movement" },
  improving: { color: "blue", label: "Improving" },
  achieved: { color: "teal", label: "Achieved" },
};

/** ObjectiveMetric → the metricDelta key that carries its direction and unit. */
const DELTA_METRIC = {
  searchPositionForKeyword: "averagePosition",
  searchPositionSiteWide: "averagePosition",
  searchClicksForKeyword: "clicks",
  searchClicksSiteWide: "clicks",
  searchImpressionsSiteWide: "impressions",
  pageviews: "pageviews",
  visitors: "visitors",
  webMentions: "mentions",
  aiAnswerPresence: "shareOfVoice",
};

const isMeasured = (state) =>
  state === "regressed" || state === "flat" || state === "improving" || state === "achieved";

const isRate = (metric) => metric === "aiAnswerPresence";

function formatValue(target) {
  const { currentValue: value, metric } = target;
  if (value == null) return "—";
  if (isRate(metric)) return `${(value * 100).toFixed(1)}%`;
  if (metric.startsWith("searchPosition")) return `#${value.toFixed(1)}`;
  return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function formatTarget(target) {
  const { targetValue: value, metric } = target;
  if (isRate(metric)) return `${(value * 100).toFixed(0)}%`;
  if (metric.startsWith("searchPosition")) return `#${value.toFixed(0)}`;
  return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

/**
 * The number the backend actually judged this target on.
 *
 * For a sampled rate that is the interval bound, not the point estimate — ObjectiveMeasurement.Meets
 * tests the bound so a rate cannot flip between met and unmet on which answers happened to be
 * sampled. The bar has to read the same value as the verdict, or a row can show a full bar beside a
 * header that counts it unmet.
 */
function judgedValue(target) {
  if (!isRate(target.metric)) return target.currentValue;

  const lowerIsBetter = METRICS[DELTA_METRIC[target.metric]]?.lowerIsBetter;
  return (lowerIsBetter ? target.intervalHigh : target.intervalLow) ?? target.currentValue;
}

/**
 * How far along the baseline → target journey this target is, 0-100.
 *
 * Returns null when there is nothing honest to draw: no measurement, or no baseline to have travelled
 * from. Works for both directions because it divides two signed distances rather than comparing
 * magnitudes.
 */
function progressPercent(target) {
  if (!isMeasured(target.state)) return null;

  const { baselineValue: from, targetValue: to } = target;
  const at = judgedValue(target);
  if (from == null || at == null) return null;

  // Achieved is achieved however the baseline sat relative to the goal. A target already past its
  // value at activation gives (to - from) the opposite sign to improvement, so every reading at or
  // better than the baseline used to clamp to 0 — a teal bar reading "0%" next to "1 of 1 met".
  if (target.state === "achieved") return 100;
  if (to === from) return null;

  // Capped below 100 so a full bar always means met, and never the other thing it could mean: a
  // rate's point estimate can sit past the goal while the interval that decides the verdict does not.
  return Math.max(0, Math.min(99, ((at - from) / (to - from)) * 100));
}

function movement(target) {
  if (!isMeasured(target.state) || target.baselineValue == null || target.currentValue == null) {
    return null;
  }

  const key = DELTA_METRIC[target.metric];
  const unit = METRICS[key]?.unit;
  const delta = target.currentValue - target.baselineValue;

  // METRICS.unit describes the DELTA, not the metric it came from: "places" and "points" want the raw
  // difference, "percent" wants a relative change. Six of the nine metrics here are "percent", so
  // passing the raw difference sent a count through the percentage formatter — impressions 1,200 →
  // 1,500 rendered as "×4.0", a claim that they quadrupled, with the arrow and colour still correct
  // so the wrong number looked plausible. Passing the baseline is what lets the shared component say
  // "was 40" rather than divide by a number too small to divide by.
  return (
    <MetricDelta
      metric={key}
      value={unit === "percent" ? (delta / target.baselineValue) * 100 : delta}
      baseline={unit === "percent" ? target.baselineValue : undefined}
      hideWhenFlat
    />
  );
}

function Provenance({ target }) {
  const parts = [];

  if (target.measuredThroughUtc) {
    // Rendered in UTC because that is what it is: a midnight-UTC day key. Left to local time it
    // reads a day early for every reader west of UTC.
    parts.push(
      `as of ${new Date(target.measuredThroughUtc).toLocaleDateString(undefined, {
        timeZone: "UTC",
      })}`,
    );
  }
  if (target.observations > 0 && target.windowDays > 0) {
    // The denominator, always visible. A rate whose n the reader cannot see is not a rate.
    parts.push(
      isRate(target.metric)
        ? `${target.observations} samples`
        : `${target.observations}/${target.windowDays} days`,
    );
  }
  if (isRate(target.metric) && target.intervalLow != null && target.intervalHigh != null) {
    // The interval, because it is what the verdict was decided on. Without it, a rate sitting past
    // its goal and still counted unmet has no visible explanation.
    parts.push(`±${(((target.intervalHigh - target.intervalLow) / 2) * 100).toFixed(0)}pp`);
  }

  if (parts.length === 0) return null;

  return (
    <Text size="xs" c="dimmed">
      {parts.join(" · ")}
    </Text>
  );
}

export default function ObjectiveScoreboard({ targets, metricLabels }) {
  if (!targets?.length) return null;

  return (
    <Table verticalSpacing="sm" horizontalSpacing="xs" withRowBorders={false}>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Target</Table.Th>
          <Table.Th ta="right">Now</Table.Th>
          <Table.Th ta="right">Goal</Table.Th>
          <Table.Th ta="right">Since start</Table.Th>
          <Table.Th>Progress</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {targets.map((target) => {
          const state = STATE[target.state] ?? STATE.flat;
          const percent = progressPercent(target);
          const measured = isMeasured(target.state);
          const label = metricLabels?.[target.metric] ?? target.metric;

          return (
            <Table.Tr key={target.id} opacity={measured ? 1 : 0.65}>
              <Table.Td>
                <Stack gap={0}>
                  <Text size="sm" fw={500}>
                    {target.subject ?? label}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {target.subject ? label : null}
                  </Text>
                </Stack>
              </Table.Td>

              <Table.Td ta="right">
                <Stack gap={0} align="flex-end">
                  <Text size="sm" fw={600} style={{ fontVariantNumeric: "tabular-nums" }}>
                    {formatValue(target)}
                  </Text>
                  <Provenance target={target} />
                </Stack>
              </Table.Td>

              <Table.Td ta="right">
                <Text size="sm" c="dimmed" style={{ fontVariantNumeric: "tabular-nums" }}>
                  {formatTarget(target)}
                </Text>
              </Table.Td>

              <Table.Td ta="right">
                <Group justify="flex-end" gap={4}>
                  {movement(target) ?? (
                    <Text size="xs" c="dimmed">
                      —
                    </Text>
                  )}
                </Group>
              </Table.Td>

              <Table.Td>
                {percent == null ? (
                  // No bar at all rather than an empty one: an empty bar reads as "no progress",
                  // which is a claim, and here we have no measurement to make it with.
                  <Tooltip label={target.stateReason ?? state.label} disabled={!target.stateReason}>
                    <Badge size="sm" variant="light" color={state.color}>
                      {state.label}
                    </Badge>
                  </Tooltip>
                ) : (
                  <Group gap="xs" wrap="nowrap">
                    <Progress
                      value={percent}
                      color={state.color}
                      size="sm"
                      radius="xl"
                      style={{ flex: 1, minWidth: 60 }}
                    />
                    <Text size="xs" c="dimmed" w={32} ta="right">
                      {Math.round(percent)}%
                    </Text>
                  </Group>
                )}
              </Table.Td>
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );
}
